import is from '@sindresorhus/is';

export type UnderlineInfo = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export function getUnderlinesInfo(
  range: Range | undefined,
  containerTop: number | undefined,
  containerLeft: number | undefined
): UnderlineInfo[] {
  const info: UnderlineInfo[] = [];

  // Return empty array if missing arguments
  if (
    is.undefined(range) ||
    is.undefined(containerTop) ||
    is.undefined(containerLeft)
  ) {
    return info;
  }

  const rects = range.getClientRects();
  for (let i = 0; i < rects.length; i++) {
    const rect = rects[i];
    info.push({
      top: rect.top - containerTop,
      left: rect.left - containerLeft,
      width: rect.width,
      height: rect.height,
    });
  }

  return info;
}

export function createRange(
  parentNode: Node | undefined,
  startOffset: number | undefined,
  endOffset: number | undefined
): Range | undefined {
  if (
    !(
      !is.undefined(parentNode) &&
      is.number(startOffset) &&
      is.number(endOffset)
    )
  ) {
    return undefined;
  }
  try {
    const { start, end } = getPosition(parentNode, startOffset, endOffset);
    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    return range;
  } catch (error) {
    console.error(error);
    return undefined;
  }
}

type Position = { node: Node; offset: number };
type PositionInfo = { start: Position; end: Position };
function getPosition(node: Node, start: number, end: number): PositionInfo {
  const nodeInfo = getNodeInfo(node);

  let startPosition: Position | null = null;
  let endPosition: Position | null = null;

  for (let i = 0; i < nodeInfo.length; i++) {
    const info = nodeInfo[i];
    if (startPosition === null) {
      if (start < info.endOffset) {
        // Add the start position
        startPosition = {
          node: info.node,
          offset: start - info.startOffset,
        };
      }
    }
    if (startPosition !== null) {
      if (end <= info.endOffset) {
        // Add the end position
        endPosition = {
          node: info.node,
          offset: end - info.startOffset,
        };
      }
    }
    if (startPosition && endPosition) {
      // Both positions have been added so we can break
      break;
    }
  }

  if (startPosition === null || endPosition === null) {
    throw new Error('NodeInfo not found for node+offset args');
  }

  return {
    start: startPosition,
    end: endPosition,
  };
}

type NodeInfo = {
  node: Node;
  text: string;
  startOffset: number;
  endOffset: number;
};

function getNodeInfo(rootNode: Node): NodeInfo[] {
  let index = 0;
  const nodes: NodeInfo[] = [];
  const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (isTextNode(node) && node.textContent !== null) {
      const newIndex = index + node.textContent.length;
      nodes.push({
        node,
        text: node.textContent,
        startOffset: index,
        endOffset: newIndex,
      });
      index = newIndex;
    }
  }

  return nodes;
}

function isTextNode(node: Node | undefined): node is Text {
  return node?.nodeType === Node.TEXT_NODE;
}
